/* eslint-env browser */
// Add / edit one record in a dialog, built from a list of field specs. It
// replaces material-table's inline row add and edit (editable.onRowAdd /
// onRowUpdate): the page keeps calling the SAME actions with the SAME data
// shape from onSubmit. Deleting is not done here: ask with confirmDialog
// (destructive, naming the record) and call the same delete action.
//
// Quiet form rules (owner, 28 Oct 2026): one label above each field, no
// helper lines on screen (a hint is the field's accessible description),
// errors under the field that is wrong once the person leaves it or presses
// Save, one primary button that shows "Saving…" while the save runs.
// Phone fields take plain digits (912345678) with +251 outside the box;
// dates and times are Addis Ababa time on a 12-hour clock.
//
//   <RecordDialog
//     open={!!editing}
//     mode={editing && editing.row ? 'edit' : 'add'}
//     title={editing && editing.row ? t('edit_reason') : t('add_reason')}
//     fields={[{ name: 'label', label: t('reason'), required: true, autoFocus: true, maxLength: 120 }]}
//     initialValues={editing && editing.row}
//     onSubmit={async (values, { mode, initialValues, changed }) => { ...same action as before... }}
//     onClose={() => setEditing(null)}
//   />
//
// onSubmit gets, for 'add', initialValues plus the fields that were filled
// (an untouched empty field is left out, as material-table did); for
// 'edit', the original record with the edited fields replaced. Resolve to
// close; throw an Error (its message is shown) to keep the dialog open.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { tokens as c } from '../../theme';
import { InlineMessage } from '../feedback';
import { FieldLabel, FieldError, SrOnly } from './FormKit';
import AddisDateTimeField from './AddisDateTimeField';
import { toNineDigits, isNineDigitMobile } from './tableUtils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NUMERIC = ['number', 'money'];
const TYPE_ALIASES = { numeric: 'number', currency: 'money', boolean: 'switch', textarea: 'multiline' };

let dialogSeq = 0;

const typeOf = (field) => TYPE_ALIASES[field.type] || field.type || 'text';

const isEmpty = (v) =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (typeof v === 'number' && Number.isNaN(v));

/** Options of a select: [{ value, label }] or a material-table lookup object. */
export const optionsOf = (field) => {
  if (Array.isArray(field.options)) return field.options;
  if (field.lookup && typeof field.lookup === 'object') {
    return Object.keys(field.lookup).map((k) => ({ value: k, label: field.lookup[k] }));
  }
  return [];
};

/** material-table's editable: 'always' | 'never' | 'onAdd' | 'onUpdate' (or false). */
const editableIn = (field, mode) => {
  const e = field.editable;
  if (e === 'never' || e === false) return false;
  if (e === 'onAdd') return mode === 'add';
  if (e === 'onUpdate') return mode !== 'add';
  return true;
};

const shownIn = (field, values, mode) => {
  if (field.hidden) return false;
  if (typeof field.show === 'function') return !!field.show(values, mode);
  return field.show !== false;
};

const initialValueOf = (field, initialValues, mode) => {
  const type = typeOf(field);
  const has = initialValues && Object.prototype.hasOwnProperty.call(initialValues, field.name);
  let v;
  if (has) v = initialValues[field.name];
  else if (field.defaultValue !== undefined) v = field.defaultValue;
  else if (mode === 'add' && field.initialEditValue !== undefined) v = field.initialEditValue;
  if (typeof field.format === 'function') v = field.format(v);
  if (type === 'switch') return !!v;
  if (type === 'phone') return toNineDigits(v);
  if (type === 'addisDateTime' || type === 'date' || type === 'custom') return v;
  if (type === 'select') {
    const match = optionsOf(field).find((o) => String(o.value) === String(v));
    return match ? match.value : '';
  }
  return v === undefined || v === null ? '' : typeof v === 'number' ? String(v) : v;
};

const outputValueOf = (field, v) => {
  const type = typeOf(field);
  let out;
  if (NUMERIC.includes(type)) out = isEmpty(v) ? null : Number(String(v).replace(/,/g, ''));
  else if (type === 'switch') out = !!v;
  else if (type === 'select') out = v === '' || v === undefined ? null : v;
  else if (type === 'addisDateTime' || type === 'date') out = isEmpty(v) ? null : Number(v);
  else if (type === 'custom') out = v;
  else out = typeof v === 'string' && field.trim !== false ? v.trim() : v;
  if (typeof field.parse === 'function') out = field.parse(out);
  return out;
};

export default function RecordDialog({
  open,
  mode = 'add',
  title,
  fields,
  initialValues,
  onSubmit,
  onClose,
  submitLabel,
  cancelLabel,
  submitColor = 'primary',
  description,
  validate,
  maxWidth = 'sm',
  closeOnSuccess = true,
  children,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const idRef = useRef(null);
  if (idRef.current === null) {
    dialogSeq += 1;
    idRef.current = `dd-record-${dialogSeq}`;
  }
  const baseId = idRef.current;
  const list = (Array.isArray(fields) ? fields : []).filter(Boolean);

  const [values, setValues] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  // Fresh values each time the dialog opens (not on every render: the
  // page's field list and record are new objects each time it renders).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const next = {};
      list.forEach((f) => {
        next[f.name] = initialValueOf(f, initialValues, mode);
      });
      setValues(next);
      setTouched({});
      setSubmitted(false);
      setBusy(false);
      setSubmitError(null);
    }
    wasOpen.current = !!open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

  const errorOf = (field, vals) => {
    if (!shownIn(field, vals, mode) || !editableIn(field, mode) || field.disabled) return null;
    const type = typeOf(field);
    const value = vals[field.name];
    if (field.required && type !== 'switch' && isEmpty(value)) {
      return field.requiredMessage || L('form_required', 'This field is required');
    }
    if (!isEmpty(value)) {
      if (type === 'email' && !EMAIL_RE.test(String(value).trim())) {
        return L('form_email_invalid', 'Enter an email address like name@example.com');
      }
      if (type === 'phone' && !isNineDigitMobile(value)) {
        return L('form_phone_invalid', 'Enter 9 digits, like 912345678');
      }
      if (NUMERIC.includes(type)) {
        const n = Number(String(value).replace(/,/g, ''));
        if (!Number.isFinite(n)) return L('form_number_invalid', 'Enter a number');
        if (field.integer && !Number.isInteger(n)) return L('form_whole_number', 'Enter a whole number');
        const hasMin = typeof field.min === 'number';
        const hasMax = typeof field.max === 'number';
        if ((hasMin && n < field.min) || (hasMax && n > field.max)) {
          if (hasMin && hasMax) return L('form_number_range', 'Enter {{min}} to {{max}}', { min: field.min, max: field.max });
          if (hasMin) return L('form_number_min', 'Enter {{min}} or more', { min: field.min });
          return L('form_number_max', 'Enter {{max}} or less', { max: field.max });
        }
      }
      if (typeof field.maxLength === 'number' && typeof value === 'string' && value.trim().length > field.maxLength) {
        return L('form_too_long', 'Use {{max}} characters or fewer', { max: field.maxLength });
      }
    }
    if (typeof field.validate === 'function') {
      const msg = field.validate(value, vals, mode);
      if (msg) return msg;
    }
    return null;
  };

  const errors = {};
  list.forEach((f) => {
    if (submitted || touched[f.name]) {
      const msg = errorOf(f, values);
      if (msg) errors[f.name] = msg;
    }
  });

  const setValue = (field, v) => {
    setValues((prev) => ({ ...prev, [field.name]: v }));
    if (submitError) setSubmitError(null);
  };
  const touch = (field) => setTouched((prev) => (prev[field.name] ? prev : { ...prev, [field.name]: true }));

  const handleClose = () => {
    if (busy) return;
    if (onClose) onClose();
  };

  const focusField = (field) => {
    const el = document.getElementById(`${baseId}-${field.name}`);
    if (el && typeof el.focus === 'function') el.focus();
  };

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (busy) return;
    setSubmitted(true);
    const firstBad = list.find((f) => errorOf(f, values));
    if (firstBad) {
      focusField(firstBad);
      return;
    }
    if (typeof validate === 'function') {
      const formError = validate(values, mode);
      if (formError) {
        setSubmitError(formError);
        return;
      }
    }
    const out = { ...(initialValues || {}) };
    const changed = [];
    list.forEach((f) => {
      if (!shownIn(f, values, mode) || !editableIn(f, mode)) return;
      const hadKey = !!initialValues && Object.prototype.hasOwnProperty.call(initialValues, f.name);
      const v = outputValueOf(f, values[f.name]);
      const blank = v === null || v === undefined || v === '';
      // An add leaves out what nobody filled (material-table did the same),
      // and never sends undefined (Firebase refuses it).
      if (blank && mode === 'add' && !hadKey) return;
      const before = hadKey ? initialValues[f.name] : undefined;
      if (String(before === undefined || before === null ? '' : before) !== String(blank ? '' : v)) changed.push(f.name);
      out[f.name] = v === undefined ? null : v;
    });
    setBusy(true);
    setSubmitError(null);
    try {
      await onSubmit(out, { mode, initialValues, changed });
      if (!mounted.current) return;
      setBusy(false);
      if (closeOnSuccess && onClose) onClose();
    } catch (err) {
      if (!mounted.current) return;
      setBusy(false);
      setSubmitError((err && err.message) || L('load_failed_body', 'Check the internet connection, then try again.'));
    }
  };

  const renderField = (field) => {
    if (!shownIn(field, values, mode)) return null;
    const type = typeOf(field);
    const id = `${baseId}-${field.name}`;
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const labelId = `${id}-label`;
    const err = errors[field.name];
    const describedBy = [field.hint ? hintId : null, err ? errorId : null].filter(Boolean).join(' ') || undefined;
    const disabled = busy || !editableIn(field, mode) || !!field.disabled;
    const value = values[field.name];
    const span = field.width === 'half' ? { xs: 'span 2', sm: 'span 1' } : 'span 2';

    if (type === 'switch') {
      return (
        <Box key={field.name} sx={{ gridColumn: span }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, minHeight: 44 }}>
            <FieldLabel htmlFor={id} id={labelId} disabled={disabled} sx={{ mb: 0 }}>
              {field.label}
            </FieldLabel>
            <Switch
              id={id}
              checked={!!value}
              disabled={disabled}
              onChange={(e) => {
                setValue(field, e.target.checked);
                touch(field);
              }}
              inputProps={{ 'aria-describedby': describedBy }}
            />
          </Box>
          {field.hint ? <SrOnly id={hintId}>{field.hint}</SrOnly> : null}
          <FieldError id={errorId}>{err}</FieldError>
        </Box>
      );
    }

    let control;
    if (type === 'addisDateTime' || type === 'date') {
      control = (
        <AddisDateTimeField
          id={id}
          value={value}
          onChange={(ms) => {
            setValue(field, ms);
            touch(field);
          }}
          disabled={disabled}
          error={!!err}
          describedBy={describedBy}
          dateOnly={type === 'date'}
          defaultTime={field.defaultTime}
        />
      );
    } else if (type === 'custom' && typeof field.render === 'function') {
      control = field.render({
        id,
        value,
        values,
        onChange: (v) => setValue(field, v),
        onBlur: () => touch(field),
        disabled,
        error: err,
        describedBy,
      });
    } else {
      const inputProps = {
        'aria-describedby': describedBy,
        'aria-invalid': err ? 'true' : undefined,
        autoComplete: field.autoComplete || 'off',
        ...(typeof field.maxLength === 'number' && !NUMERIC.includes(type) ? { maxLength: field.maxLength } : {}),
      };
      const common = {
        id,
        fullWidth: true,
        value: value === undefined || value === null ? '' : value,
        disabled,
        error: !!err,
        onBlur: () => touch(field),
        autoFocus: !!field.autoFocus && !disabled,
        placeholder: field.placeholder,
      };
      if (type === 'select') {
        control = (
          <TextField
            {...common}
            select
            onChange={(e) => setValue(field, e.target.value)}
            SelectProps={{ labelId, SelectDisplayProps: { 'aria-describedby': describedBy } }}
            inputProps={{ 'aria-invalid': err ? 'true' : undefined }}
          >
            {field.required ? null : (
              <MenuItem value="">
                <Box component="span" sx={{ color: c.TEXT_3 }}>
                  {field.emptyLabel || L('form_none', 'None')}
                </Box>
              </MenuItem>
            )}
            {optionsOf(field).map((o) => (
              <MenuItem key={String(o.value)} value={o.value} disabled={!!o.disabled}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        );
      } else if (type === 'phone') {
        control = (
          <TextField
            {...common}
            type="tel"
            onChange={(e) => setValue(field, toNineDigits(e.target.value))}
            placeholder={field.placeholder || '912345678'}
            inputProps={{ ...inputProps, inputMode: 'numeric', maxLength: 9, autoComplete: 'off' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ color: c.INK_2, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    +251
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        );
      } else {
        const numeric = NUMERIC.includes(type);
        control = (
          <TextField
            {...common}
            type={type === 'email' ? 'email' : 'text'}
            multiline={type === 'multiline'}
            minRows={type === 'multiline' ? field.rows || 3 : undefined}
            onChange={(e) => setValue(field, e.target.value)}
            inputProps={{
              ...inputProps,
              ...(numeric ? { inputMode: field.integer ? 'numeric' : 'decimal' } : {}),
              ...(type === 'email' ? { inputMode: 'email', spellCheck: false } : {}),
            }}
            InputProps={
              field.adornment
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box component="span" sx={{ color: c.INK_2, fontWeight: 600 }}>
                          {field.adornment}
                        </Box>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
            sx={numeric ? { '& input': { fontVariantNumeric: 'tabular-nums' } } : undefined}
          />
        );
      }
    }

    return (
      <Box key={field.name} sx={{ gridColumn: span, minWidth: 0 }}>
        <FieldLabel htmlFor={type === 'select' ? undefined : id} id={labelId} disabled={disabled}>
          {field.label}
        </FieldLabel>
        {control}
        {field.hint ? <SrOnly id={hintId}>{field.hint}</SrOnly> : null}
        <FieldError id={errorId}>{err}</FieldError>
      </Box>
    );
  };

  const titleId = `${baseId}-title`;

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={phone}
      aria-labelledby={titleId}
      PaperProps={{ component: 'form', noValidate: true, onSubmit: handleSubmit }}
    >
      <DialogTitle id={titleId} sx={{ pr: 8 }}>
        {title}
        <IconButton aria-label={L('close_dialog', 'Close')} onClick={handleClose} disabled={busy} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {description ? (
          <Typography variant="body2" sx={{ color: c.INK_2, mb: 2 }}>
            {description}
          </Typography>
        ) : null}
        {submitError ? (
          <InlineMessage kind="error" title={L('form_save_failed', 'Could not save')} sx={{ mb: 2 }}>
            {submitError}
          </InlineMessage>
        ) : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 2, rowGap: 2.25 }}>
          {list.map(renderField)}
        </Box>
        {children}
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="neutral" onClick={handleClose} disabled={busy}>
          {cancelLabel || L('cancel', 'Cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color={submitColor === 'success' ? 'success' : 'primary'}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {busy ? L('form_saving', 'Saving…') : submitLabel || L('save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
